/**
 * NY Hardware — Multi-Touch Attribution Capture
 * ------------------------------------------------
 * What this does:
 *   1. On each qualifying pageview, reads UTM params + ad click IDs from the URL.
 *   2. Appends a "touch" to a first-party cookie (only for consented visitors).
 *   3. Pushes the running journey to Shopify CART ATTRIBUTES in the background —
 *      NOT at checkout click time — so there is zero added latency at checkout.
 *      Cart attributes automatically carry over to the Order once checkout
 *      completes, so no checkout.liquid / checkout-extensibility access is needed.
 *
 * Where to put this file:
 *   - Upload as an asset in your theme: Online Store > Themes > Edit code > Assets > Add a new asset
 *     (name it nyhw-attribution.js)
 *   - In layout/theme.liquid, right before </body>, add:
 *       {{ 'nyhw-attribution.js' | asset_url | script_tag }}
 *     (asset_url + script_tag loads it non-render-blocking; no "defer" needed on top of that,
 *      but it doesn't hurt to add defer="defer" to the tag if your theme helper allows it)
 *
 * Consent:
 *   - Uses Shopify's built-in Customer Privacy API if present (works with Shopify's native
 *     consent banner AND most CMPs that integrate with it, e.g. via the Consent API).
 *   - If you use a third-party CMP that does NOT wire into Shopify's Customer Privacy API,
 *     set window.NYHW_hasMarketingConsent = true/false yourself when consent changes —
 *     see the fallback block below. Default is FALSE (no tracking) until consent is confirmed.
 */
(function () {
  "use strict";

  // ---------- CONFIG ----------
  var COOKIE_NAME = "nyhw_mta";           // first-party cookie holding the journey
  var COOKIE_DAYS = 90;                    // requested lifetime (Safari ITP caps JS-set
                                            // cookies at 7 days regardless — no workaround
                                            // for that within a compliant, non-fingerprinting setup)
  var MAX_TOUCHES = 15;                    // cap array size; first touch is always preserved
  var CART_ATTR_JOURNEY = "attribution_journey";
  var CART_ATTR_FIRST = "attribution_first_touch";
  var CART_ATTR_LAST = "attribution_last_touch";
  var CART_ATTR_GA_CLIENT_ID = "attribution_ga_client_id";
  var SESSION_KEY = "nyhw_last_activity_ts";
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min inactivity = new session (GA4's default convention)

  // Params we treat as "this pageview is a marketing touch"
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  var CLICK_ID_KEYS = {
    gclid: "google_ads",
    gbraid: "google_ads",
    wbraid: "google_ads",
    fbclid: "meta",
    msclkid: "microsoft_ads",
    ttclid: "tiktok",
    epik: "pinterest",
    pin_click_id: "pinterest"
  };

  // Known referrer domains for fallback channel classification when no UTM/click ID is present
  var SEARCH_ENGINES = ["google.", "bing.", "yahoo.", "duckduckgo.", "ecosia."];
  var SOCIAL_DOMAINS = ["facebook.com", "instagram.com", "pinterest.", "tiktok.com", "twitter.com", "x.com", "linkedin.com"];

  // Shopify's own infrastructure — checkout, accounts, and Shop Pay run on a DIFFERENT
  // domain than the storefront (checkout.shopify.com, your-store.myshopify.com, shop.app),
  // so a shopper returning from checkout looks like an external referral unless we treat
  // these the same as same-site navigation. Not a new marketing touch, just a continuation
  // of the same shopping session.
  var SHOPIFY_INFRASTRUCTURE_DOMAINS = ["shopify.com", "myshopify.com", "shop.app"];

  // AI chat interfaces — a human clicked a link surfaced in an AI answer. Straightforward to
  // detect (referrer domain), and behaves like a normal human visit otherwise.
  var AI_REFERRER_DOMAINS = {
    "chat.openai.com": "chatgpt",
    "chatgpt.com": "chatgpt",
    "perplexity.ai": "perplexity",
    "gemini.google.com": "gemini",
    "claude.ai": "claude",
    "copilot.microsoft.com": "copilot"
    // Note: Bing Copilot/chat can't be reliably separated from regular Bing search via
    // hostname alone (both are www.bing.com) — not included here to avoid false positives.
  };

  // Live/agentic AI browsing — an AI agent is loading the page on someone's behalf, sometimes
  // through to checkout. BEST-EFFORT ONLY: several platforms distinguish a "live, user-triggered
  // fetch" token from their own training/indexing crawler's user-agent string, and those are
  // what this list targets. Many agentic browsers deliberately use ordinary browser UA strings
  // for site compatibility, so a miss here does NOT mean a visit wasn't agent-driven — treat
  // this as a floor, not a ceiling, on agentic traffic. This list will likely need periodic
  // review as the space evolves quickly.
  var AI_AGENT_UA_TOKENS = {
    "ChatGPT-User": "chatgpt",
    "OAI-SearchBot": "chatgpt",
    "Claude-User": "claude",
    "Claude-SearchBot": "claude",
    "Perplexity-User": "perplexity",
    "PerplexityBot": "perplexity"
  };

  function detectAiAgent() {
    var ua = navigator.userAgent || "";
    for (var token in AI_AGENT_UA_TOKENS) {
      if (ua.indexOf(token) !== -1) {
        return { isAgent: true, platform: AI_AGENT_UA_TOKENS[token] };
      }
    }
    return { isAgent: false, platform: null };
  }

  // ---------- CONSENT ----------
  function hasMarketingConsent(callback) {
    // Explicit manual override always wins (for non-Shopify-native CMPs)
    if (typeof window.NYHW_hasMarketingConsent === "boolean") {
      return callback(window.NYHW_hasMarketingConsent);
    }

    // Shopify's Customer Privacy API (works with Shopify's native banner + CMPs that integrate with it,
    // e.g. Avada). Gated on ANALYTICS consent only — this data stays first-party and is used for
    // internal measurement, not ad delivery/targeting, so analytics consent alone is treated as sufficient.
    // (Outbound pushes to ad-platform Conversions APIs, if added later, should be gated on marketing
    // consent separately at that step.)
    //
    // IMPORTANT: window.Shopify.customerPrivacy may not be attached yet at the exact moment
    // DOMContentLoaded fires — its load timing depends on script order/network conditions and
    // varies pageview to pageview. Checking once and giving up causes intermittent silent
    // under-counting (worked on some test loads, missed others, no error thrown). We poll
    // briefly instead of a single check.
    var attempts = 0;
    var maxAttempts = 20; // 20 x 150ms = 3s max wait before giving up
    function poll() {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        try {
          var analytics = window.Shopify.customerPrivacy.analyticsProcessingAllowed();
          return callback(!!analytics);
        } catch (e) {
          return callback(false);
        }
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 150);
      } else {
        // Gave up waiting — treat as no consent signal (privacy-safe default), but this
        // should be rare; if it happens often, Shopify's privacy script itself may be
        // failing to load, which is worth investigating separately.
        callback(false);
      }
    }
    return poll();
  }

  // React live if the shopper changes their consent choice mid-session (Shopify fires this event)
  document.addEventListener("visitorConsentCollected", function () {
    hasMarketingConsent(function (allowed) {
      if (allowed) captureTouchIfQualifying();
      // Note: we don't retroactively delete an existing cookie here — if a merchant needs
      // "withdraw consent = purge cookie" behavior, add: if (!allowed) deleteCookie(COOKIE_NAME);
    });
  });

  // ---------- COOKIE HELPERS ----------
  function getCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie =
      name + "=" + encodeURIComponent(value) +
      "; expires=" + expires +
      "; path=/; SameSite=Lax; Secure";
  }

  function deleteCookie(name) {
    document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  }

  // ---------- GA4 CLIENT ID (for joining orders to GA4 BigQuery export sessions) ----------
  // GA4 sets a first-party _ga cookie in the format "GA1.2.<part1>.<part2>", where
  // "<part1>.<part2>" is the same value GA4's BigQuery export stores as user_pseudo_id.
  // Capturing it lets Colab join a specific order directly to its GA4 session history —
  // no dependency on gtag being loaded or knowing the GA4 measurement ID.
  function getGa4ClientId() {
    var match = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
    return match ? match[1] : null;
  }

  function readJourney() {
    var raw = getCookie(COOKIE_NAME);
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeJourney(touches) {
    setCookie(COOKIE_NAME, JSON.stringify(touches), COOKIE_DAYS);
  }

  function trimTouches(touches) {
    if (touches.length <= MAX_TOUCHES) return touches;
    // Always keep the very first touch (true first-touch attribution), then the most recent ones
    return [touches[0]].concat(touches.slice(touches.length - (MAX_TOUCHES - 1)));
  }

  // ---------- DEVICE / BROWSER DETECTION ----------
  // Heuristic UA-based detection — not perfect (UA strings can be spoofed or frozen by
  // browser privacy features), but sufficient for directional device-mix / cross-device
  // analysis and for flagging Safari sessions (7-day cookie cap) vs. others.
  function detectClient() {
    var ua = navigator.userAgent || "";

    var isTablet = /iPad|Tablet|Nexus 7|Nexus 10|KFAPWI/i.test(ua) ||
      (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua)); // iPadOS reports as Mac
    var isMobile = !isTablet && /Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    var deviceType = isTablet ? "tablet" : (isMobile ? "mobile" : "desktop");

    var isEdge = /Edg\//.test(ua);
    var isChrome = /Chrome|CriOS/.test(ua) && !isEdge;
    var isFirefox = /Firefox|FxiOS/.test(ua);
    var isSafari = /Safari/.test(ua) && !isChrome && !isEdge && !isFirefox && !/Android/.test(ua);
    var browser = isEdge ? "edge" : isChrome ? "chrome" : isFirefox ? "firefox" : isSafari ? "safari" : "other";

    return { device_type: deviceType, browser: browser };
  }

  // ---------- URL / CHANNEL PARSING ----------
  function getQueryParams() {
    var params = {};
    var search = window.location.search.substring(1).split("&");
    for (var i = 0; i < search.length; i++) {
      if (!search[i]) continue;
      var pair = search[i].split("=");
      params[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
    }
    return params;
  }

  function classifyReferrer(referrer) {
    if (!referrer) return null; // no referrer info available — let session/click-id/UTM logic decide
    var host = "";
    try {
      host = new URL(referrer).hostname.toLowerCase();
    } catch (e) {
      return { source: "unknown", medium: "referral" };
    }
    if (host.indexOf(window.location.hostname) !== -1) return null; // internal navigation, not a new touch

    for (var s = 0; s < SHOPIFY_INFRASTRUCTURE_DOMAINS.length; s++) {
      if (host.indexOf(SHOPIFY_INFRASTRUCTURE_DOMAINS[s]) !== -1) return null; // checkout/accounts/Shop Pay — same session
    }

    for (var aiDomain in AI_REFERRER_DOMAINS) {
      if (host.indexOf(aiDomain) !== -1) {
        return { source: AI_REFERRER_DOMAINS[aiDomain], medium: "ai_referral" };
      }
    }

    for (var i = 0; i < SEARCH_ENGINES.length; i++) {
      if (host.indexOf(SEARCH_ENGINES[i]) !== -1) return { source: host, medium: "organic_search" };
    }
    for (var j = 0; j < SOCIAL_DOMAINS.length; j++) {
      if (host.indexOf(SOCIAL_DOMAINS[j]) !== -1) return { source: host, medium: "organic_social" };
    }
    return { source: host, medium: "referral" };
  }

  // Session boundary based on inactivity, not tab lifetime — a tab left open across a
  // multi-day gap should still count as separate visits, and a 30-min-timeout definition
  // matches the convention most attribution/analytics tools (e.g. GA4) already use.
  function isNewSession() {
    var now = Date.now();
    var lastActivity = sessionStorage.getItem(SESSION_KEY);
    var isNew = !lastActivity || (now - parseInt(lastActivity, 10) > SESSION_TIMEOUT_MS);
    sessionStorage.setItem(SESSION_KEY, String(now)); // always refresh, whether new session or not
    return isNew;
  }

  function buildTouchFromCurrentPage(existingTouches) {
    var qp = getQueryParams();

    var clickIdKey = null, clickIdPlatform = null;
    for (var key in CLICK_ID_KEYS) {
      if (qp[key]) {
        clickIdKey = key;
        clickIdPlatform = CLICK_ID_KEYS[key];
        break;
      }
    }
    var hasClickId = !!clickIdKey;

    // A touch is recorded ONLY at the start of a new session (first pageview of a visit,
    // or returning after 30+ min of inactivity). Every other pageview within that same
    // session — browsing collections, products, search — is intentionally NOT a new touch,
    // even if a click ID happens to still be present in the URL via link decoration
    // (e.g. Google's auto-tagging persisting gclid across on-site navigation).
    if (!isNewSession()) {
      return null;
    }

    var refClassification = classifyReferrer(document.referrer);
    var agentCheck = detectAiAgent();

    // Tab-open re-entry: a NEW session (30+ min gap, or a closed/reopened tab) with NO
    // explicit signal (no UTM, no click ID, no self-identifying agent, no classifiable
    // referrer) used to fall back to generic "direct" — but if this cookie already has
    // prior touches, this isn't a fresh, contextless visitor; it's someone picking back up
    // a journey that was already in progress. That's a meaningfully different behavior
    // (long research/consideration cycles) worth its own bucket, with the prior touch's
    // channel and timing carried forward so you can see what originally brought them in
    // and how long the gap was.
    var hasExplicitSignal = !!qp.utm_source || hasClickId || agentCheck.isAgent || !!refClassification;
    var hasPriorHistory = !!(existingTouches && existingTouches.length > 0);
    var isTabReentry = !hasExplicitSignal && hasPriorHistory;
    var lastTouch = hasPriorHistory ? existingTouches[existingTouches.length - 1] : null;

    var touch = {
      ts: new Date().toISOString(),
      landing_page: window.location.pathname,
      referrer: document.referrer || null,
      device_type: detectClient().device_type,
      browser: detectClient().browser,
      is_ai_agent: agentCheck.isAgent,           // best-effort — see AI_AGENT_UA_TOKENS notes above
      ai_agent_platform: agentCheck.platform,    // e.g. "chatgpt", "claude", "perplexity", or null
      is_tab_reentry: isTabReentry,
      previous_touch_source: isTabReentry ? lastTouch.utm_source : null,
      previous_touch_medium: isTabReentry ? lastTouch.utm_medium : null,
      previous_touch_campaign: isTabReentry ? lastTouch.utm_campaign : null,
      previous_touch_ts: isTabReentry ? lastTouch.ts : null,
      // Priority: explicit UTM > ad click ID > self-identifying AI agent > classified referrer
      // (including AI chat referrals) > tab re-entry > direct fallback
      utm_source: qp.utm_source || (hasClickId ? clickIdPlatform : null) || (agentCheck.isAgent ? agentCheck.platform : null) || (refClassification && refClassification.source) || (isTabReentry ? "returning_visitor" : "direct"),
      utm_medium: qp.utm_medium || (hasClickId ? "cpc" : null) || (agentCheck.isAgent ? "ai_agent" : null) || (refClassification && refClassification.medium) || (isTabReentry ? "tab_reentry" : "none"),
      utm_campaign: qp.utm_campaign || null,
      utm_term: qp.utm_term || null,
      utm_content: qp.utm_content || null,
      click_id_type: clickIdKey,
      click_id_value: clickIdKey ? qp[clickIdKey] : null
    };

    return touch;
  }

  // ---------- CART SYNC (background, non-blocking, not tied to checkout click) ----------
  function syncToCart(touches) {
    if (!touches.length) return;
    var payload = {
      attributes: {}
    };
    payload.attributes[CART_ATTR_JOURNEY] = JSON.stringify(touches);
    payload.attributes[CART_ATTR_FIRST] = JSON.stringify(touches[0]);
    payload.attributes[CART_ATTR_LAST] = JSON.stringify(touches[touches.length - 1]);

    var gaClientId = getGa4ClientId();
    if (gaClientId) {
      payload.attributes[CART_ATTR_GA_CLIENT_ID] = gaClientId;
    }

    // Fire-and-forget: runs in the background on pageview, so nothing is added
    // to the critical path at checkout time.
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true // lets the request complete even if the user navigates away immediately
    }).catch(function () {
      // Fail silently — worst case this pageview's touch stays in the cookie only
      // and gets synced on the next pageview or cart update.
    });
  }

  // ---------- MAIN ----------
  function captureTouchIfQualifying() {
    var touches = readJourney();
    var newTouch = buildTouchFromCurrentPage(touches);

    if (newTouch) {
      touches.push(newTouch);
      touches = trimTouches(touches);
      writeJourney(touches);
    }

    if (touches.length) {
      syncToCart(touches);
    }
  }

  function init() {
    hasMarketingConsent(function (allowed) {
      if (!allowed) return; // respects opt-out / no consent yet — nothing is set or sent
      captureTouchIfQualifying();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();